/** @format */

import { produce } from "immer";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { useAppSelector } from "../../app/hooks";
import { useLocale } from "../i18n/hooks";
import {
  difficultyLevel,
  Image,
  Lesson,
  Sublesson,
  UserResource,
} from "../resources/types";
import {
  selectItemId,
  selectStage,
  setIsLoading,
  setShowModal,
  setStage,
} from "./contentEditorSlice";
import { ReactComponent as Delete } from "bootstrap-icons/icons/trash.svg";
import { ReactComponent as CaretUp } from "bootstrap-icons/icons/chevron-up.svg";
import { ReactComponent as CaretDown } from "bootstrap-icons/icons/chevron-down.svg";
import "./addLesson.scss";
import ContentEditorFooter from "./contentEditorFooter";
import { useDispatch } from "react-redux";
import ImageSelector from "./imageSelector";
import ContentEditorHeader from "./contentEditorHeader";
import { useSaveUserResourceMutation } from "../../app/services/userResource/userResourcesApi";
import { checkSublesson as checkUserSubLesson } from "../userResources/userResourceFunctions";
import {
  checkLesson,
  checkSublesson as checkResourceSubLesson,
  isValidUuid,
} from "../resources/ResourceFunctions";
import { LocaleText } from "../i18n/init";
import { useUpdateResourceMutation } from "../../app/services/resource/resourcesApi";
import { ReactComponent as ExclamationCircle } from "bootstrap-icons/icons/exclamation-circle.svg";
import { useTranslation } from "react-i18next";
import { ReactComponent as Asterisk } from "bootstrap-icons/icons/asterisk.svg";
import { createApi } from "unsplash-js";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetUserQuery } from "../../app/services/userApi";

type Props = {
  resource?: UserResource;
  accountType?: string;
  dev?: boolean;
};

export type LessonData = {
  uuid?: string;
  title: Partial<LocaleText>;
  heroImage: Image;
  difficultyLevels: difficultyLevel[];
  subLessons: (Omit<Sublesson, "title" | "slug" | "uuid"> & {
    title: Partial<LocaleText>;
    slug: string;
    uuid?: string;
  })[];
};

const AddLesson: React.FC<Props> = ({
  resource,
  accountType = "teacher",
  dev = false,
}) => {
  const [data, setData] = useState<LessonData>({
    title: { en: "", cy: "" },
    heroImage: {
      src: "",
      $metadata: {
        positionY: 50,
      },
    },
    difficultyLevels: [],
    subLessons: [],
  });
  const locale = useLocale();
  const { t: _ } = useTranslation();
  const dispatch = useDispatch();
  const [isHeroValid, setIsHeroValid] = useState<boolean | undefined>(
    undefined,
  );
  const step = useAppSelector(selectStage());
  const itemId = useAppSelector(selectItemId());

  const [subLessonDeleteId, setSubLessonDeleteId] = useState<
    number | undefined
  >();
  const [validated, setValidated] = useState(false);

  const [saveUserResource] = useSaveUserResourceMutation();
  const [saveResource] = useUpdateResourceMutation();
  const onChangeValidated = useCallback((value: boolean) => {
    setValidated(value);
  }, []);

  const isNewLesson = !!itemId ? !isValidUuid(itemId) : false;
  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const { data: user, isLoading } = useGetUserQuery(
    !!isAuthenticated ? undefined : skipToken,
  );
  const isStaff = !isLoading && user !== undefined && (user.role === "wjec" || user.role === "admin");

  const token = useAppSelector((state) => state.auth.userToken?.token);

  const unsplash = createApi({
    apiUrl: `${
      process.env.REACT_APP_RESOURCES_API_URL ??
      "https://36h5qfaq9c.execute-api.eu-west-2.amazonaws.com/dev/"
    }unsplash`,
    headers: {
      authorization: `Bearer ${token}`,
    },
  });

  useEffect(() => {
    if (itemId && resource) {
      const lesson: Lesson | undefined = resource.Object.lessons?.find(
        (item) => item.uuid === itemId,
      );
      if (lesson) {
        const emptyLocaleText: LocaleText = {
          en: "",
          cy: "",
        };

        let lessonTitle: Partial<LocaleText> = { ...emptyLocaleText };

        if (typeof lesson.title === "string") {
          lessonTitle[locale] = lesson.title;
        } else {
          lessonTitle = lesson.title;
        }

        setData((prev) => ({
          ...prev,
          uuid: lesson.uuid ?? undefined,
          title: lessonTitle ?? "",
          heroImage:
            typeof lesson.heroImage === "object"
              ? lesson.heroImage
              : { src: lesson.heroImage },
          difficultyLevels: lesson?.difficultyLevels || [],
          subLessons: lesson?.subLessons.map((item, index) => ({
            uuid: item.uuid ?? undefined,
            slug: item.slug ?? "slug",
            title:
              typeof item.title === "object"
                ? item.title
                : { ...emptyLocaleText, [locale]: item.title },
            cards: item.cards,
          })),
        }));
      }
    }
  }, [resource, itemId, locale]);

  const handleUpdateSubLesson = (value: string, id: number) => {
    if (!resource) return;

    setData(
      produce((draft) => {
        if (draft.subLessons[id] !== undefined) {
          draft.subLessons[id] = resource.PK.includes("USER_BL#")
            ? checkUserSubLesson({
                ...draft.subLessons[id],
                title: { ...draft.subLessons[id].title, [locale]: value },
              })
            : checkResourceSubLesson(
                {
                  ...draft.subLessons[id],
                  title: { ...draft.subLessons[id].title, [locale]: value },
                },
                locale,
              );
        } else {
          draft.subLessons.push(
            resource.PK.includes("USER_BL#")
              ? checkUserSubLesson({
                  title: { en: "", cy: "" },
                  cards: [],
                  slug: "slug",
                  originalUUID: "",
                })
              : checkResourceSubLesson(
                  {
                    title: { en: "", cy: "" },
                    cards: [],
                    slug: "slug",
                  },
                  locale,
                ),
          );
        }
        onChangeValidated(false);
      }),
    );
  };

  const upChoice = (index: number) => {
    setData(
      produce((draft) => {
        if (index !== 0) {
          const content = draft.subLessons[index - 1];
          draft.subLessons[index - 1] = draft.subLessons[index];
          draft.subLessons[index] = content;
          if (subLessonDeleteId !== undefined) {
            if (subLessonDeleteId === index) {
              setSubLessonDeleteId(index - 1);
            } else if (subLessonDeleteId === index - 1) {
              setSubLessonDeleteId(index);
            }
          }
        }
      }),
    );
  };

  const downChoice = (index: number) => {
    setData(
      produce((draft) => {
        if (index !== draft.subLessons.length - 1) {
          const content = draft.subLessons[index + 1];
          draft.subLessons[index + 1] = draft.subLessons[index];
          draft.subLessons[index] = content;
          if (subLessonDeleteId !== undefined) {
            if (subLessonDeleteId === index) {
              setSubLessonDeleteId(index + 1);
            } else if (subLessonDeleteId === index + 1) {
              setSubLessonDeleteId(index);
            }
          }
        }
      }),
    );
  };

  // TODO: Complete Remove Functionality of Sub Lessons on Lesson Page Modal
  const removeSubLesson = (id: string) => {
    setData(
      produce((draft) => {
        draft.subLessons.splice(parseInt(id), 1);
      }),
    );
    onChangeValidated(false);
  };

  const handleDifficultyLevelChange = (
    level: difficultyLevel,
    checked: boolean,
  ) => {
    setData(
      produce((draft) => {
        const index = data.difficultyLevels.indexOf(level);
        if (checked && index === -1) draft.difficultyLevels.push(level);
        else if (!checked && index > -1)
          draft.difficultyLevels.splice(index, 1);
      }),
    );
  };

  const handleHeroUrlChange = (target: HTMLInputElement) => {
    if (accountType !== "teacher") {
      const isValid = Boolean(
        !target.value ||
          (target.checkValidity() && target.value.startsWith("https://")),
      );
      setIsHeroValid(isValid);
    }

    setData((prev) => ({
      ...prev,
      heroImage: { ...prev.heroImage, src: target.value },
    }));
  };

  const handleChangeHeroImage = (image: Image) => {
    setData((prev) => ({ ...prev, heroImage: image }));
  };

  // TODO: Handle Saving of Form
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (!resource) return;
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    let valid = true;

    if (form.checkValidity() === true) {
      if (step === 1) {
        valid = false;
        dispatch(setStage(step + 1));
      } else {
        if (!dev && itemId) {
          dispatch(setIsLoading(true));

          if (
            data.heroImage?.$metadata?.downloadSrc &&
            data.heroImage.$metadata.downloadSrc.includes("unsplash.com")
          ) {
            unsplash.photos.trackDownload({
              downloadLocation: data.heroImage.$metadata.downloadSrc,
            });
          }

          let _resource;
          if (!isNewLesson) {
            _resource = produce(resource, (draft) => {
              let lessonIndex = draft.Object.lessons.findIndex(
                (lesson) => lesson.uuid === itemId,
              );
              if (lessonIndex >= 0)
                draft.Object.lessons[lessonIndex] = checkLesson(data as Lesson);
            });
          } else {
            _resource = produce(resource, (draft) => {
              draft.Object.lessons.splice(
                parseInt(itemId),
                0,
                checkLesson(data as Lesson),
              );
            });
          }

          if (resource.PK.includes("USER_BL#") && resource.Copy !== undefined) {
            await saveUserResource({
              primaryKey: {
                PK: resource.PK,
                SK: resource.SK,
              },
              diff: { Object: { lessons: _resource.Object.lessons } },
              userEmailHash: resource.UserEmailHash,
              copy: resource.Copy,
              locale: locale,
            });
          } else {
            await saveResource({
              primaryKey: {
                PK: resource.PK,
                SK: resource.SK,
              },
              diff: { Object: { lessons: _resource.Object.lessons } },
            });
          }
        }
        dispatch(setShowModal(false));
      }
    }

    setValidated(valid);
  };

  const renderTooltip = (props: any) => (
    <Tooltip {...props}>Required field</Tooltip>
  );

  return (
    <>
      <ContentEditorHeader />
      <Modal.Body className="content-editor-body p-0">
        <Form
          role="form"
          onSubmit={handleSubmit}
          validated={validated}
          noValidate
          className="lesson-modal-content unselectable"
        >
          {step === 1 && (
            <div>
              <p>{_("Please enter the Lesson details:")}</p>
              <p>{_("Required fields are marked with an asterisk")}</p>
              <br />
              <Row>
                <Form.Label htmlFor="lesson-title" column="lg" xs={12} lg={2}>
                  <span style={{ color: "red" }}>*</span> {_("Lesson title")}
                </Form.Label>
                <Col xs={12} lg={10}>
                  <Form.Control
                    id="lesson-title"
                    name="title"
                    type="text"
                    className="form-control form-control-lg"
                    value={
                      (typeof data.title === "object"
                        ? data.title?.[locale]
                        : data?.title) ?? ""
                    }
                    onChange={(e) =>
                      setData((prev) => ({
                        ...prev,
                        title: { ...prev.title, [locale]: e.target.value },
                      }))
                    }
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {_("Please enter a title.")}
                  </Form.Control.Feedback>
                </Col>
              </Row>
              <Row className="align-items-center my-3">
                <Form.Label
                  htmlFor="difficulty-foundation"
                  column="lg"
                  xs={12}
                  lg={2}
                >
                  {_("Difficulty")}
                </Form.Label>
                <Col xs={12} lg={10}>
                  <Form.Group className="difficulty-checkboxes">
                    <Form.Check
                      type="checkbox"
                      id="difficulty-foundation"
                      className="prevent-validation"
                      label={
                        <>
                          <span className=" d-inline-block difficulty-foundation rounded-circle"></span>{" "}
                          {_("Foundation")}
                        </>
                      }
                      inline
                      checked={data.difficultyLevels.includes("foundation")}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleDifficultyLevelChange(
                          "foundation",
                          e.currentTarget.checked,
                        )
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      id="difficulty-intermediate"
                      label={
                        <>
                          <span className="d-inline-block difficulty-intermediate rounded-circle"></span>{" "}
                          {_("Intermediate")}
                        </>
                      }
                      inline
                      className="prevent-validation"
                      checked={data.difficultyLevels.includes("intermediate")}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleDifficultyLevelChange(
                          "intermediate",
                          e.currentTarget.checked,
                        )
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      id="difficulty-higher"
                      label={
                        <>
                          <span className="d-inline-block difficulty-higher rounded-circle"></span>{" "}
                          {_("Higher")}
                        </>
                      }
                      inline
                      className="prevent-validation"
                      checked={data.difficultyLevels.includes("higher")}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleDifficultyLevelChange(
                          "higher",
                          e.currentTarget.checked,
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label column="lg">
                      <span style={{ color: "red" }}>*</span>
                      {_("Lesson Image")}
                    </Form.Label>
                    <p>
                      {_(
                        "Select one of the images or patterns below to feature as your lesson image:",
                      )}
                    </p>
                    <ImageSelector
                      image={data.heroImage}
                      onChangeImage={handleChangeHeroImage}
                      heroImageOnly={isStaff}
                    />
                    {isStaff ? (
                      <>
                        <Form.Control
                          id="lesson-image"
                          aria-label={`lesson-image-url`}
                          value={data.heroImage.src ?? ""}
                          isInvalid={isHeroValid === false}
                          type="url"
                          required
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleHeroUrlChange(e.currentTarget)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {_("Please provide a lesson image.")}
                        </Form.Control.Feedback>
                      </>
                    ) : (
                      <Form.Control
                        required
                        hidden
                        value={data.heroImage.src ?? ""}
                        onChange={() => {}}
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
          {step === 2 && (
            <div>
              {data.subLessons.length === 0 ? (
                <div className="no-items-message d-flex w-100 justify-content-center flex-column text-center py-5">
                  <h2>{_("This lesson currently has no sub-lessons")}</h2>
                  <p>{_("To add a sub-lessons press the '+' button below.")}</p>

                  <Form.Control hidden required />
                  {validated && (
                    <p className="text-danger">
                      <ExclamationCircle
                        style={{ verticalAlign: "-0.125em" }}
                      />{" "}
                      {_("You must add a sublesson before saving a lesson")}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <p>
                    <strong>{_("Please enter the Lesson details:")}</strong>
                  </p>
                  <p>{_("Required fields are marked with an asterisk")}</p>
                </div>
              )}
              {data.subLessons.map(
                (
                  subLesson: Omit<Sublesson, "title" | "uuid" | "slug"> & {
                    title: Partial<LocaleText> | string;
                    uuid?: string;
                    slug?: string;
                  },
                  index: number,
                ) => (
                  <div
                    key={index}
                    className="d-flex w-100 gap-3 mb-2 align-items-center"
                  >
                    <CaretUp
                      className={`element-action ${
                        validated &&
                        ((typeof subLesson.title === "object"
                          ? subLesson?.title[locale]
                          : subLesson?.title) ?? "") === "" &&
                        "mb-4"
                      } ${
                        data.subLessons.length <= 1 || index === 0
                          ? "disabled"
                          : ""
                      } `}
                      onClick={() => upChoice(index)}
                    />
                    <CaretDown
                      className={`element-action ${
                        validated &&
                        ((typeof subLesson.title === "object"
                          ? subLesson?.title[locale]
                          : subLesson?.title) ?? "") === "" &&
                        "mb-4"
                      } ${
                        data.subLessons.length <= 1 ||
                        index === data.subLessons.length - 1
                          ? "disabled"
                          : ""
                      }`}
                      onClick={() => downChoice(index)}
                    />
                    <Form.Group className="flex-grow-1">
                      <InputGroup>
                        <Form.Control
                          id={`subLesson-title-${index}`}
                          name="subLesson Title"
                          value={
                            (typeof subLesson.title === "object"
                              ? subLesson?.title[locale]
                              : subLesson?.title) ?? ""
                          }
                          type="text"
                          onChange={(e) =>
                            handleUpdateSubLesson(e.currentTarget.value, index)
                          }
                          aria-label={`subLesson-title-${index}`}
                          required
                          className={
                            subLessonDeleteId === index
                              ? "border-primary"
                              : undefined
                          }
                          placeholder={_("Sub-lesson title")}
                        />
                        <OverlayTrigger
                          placement="right"
                          overlay={renderTooltip}
                        >
                          <InputGroup.Text>
                            <span style={{ color: "red" }}>
                              <Asterisk style={{ width: "10px" }} />
                            </span>
                          </InputGroup.Text>
                        </OverlayTrigger>
                      </InputGroup>

                      <Form.Control.Feedback type="invalid">
                        {_("Please enter a sub-lesson title.")}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <div
                      className="d-flex align-items-center"
                      title={
                        data.subLessons.length <= 1
                          ? _("Minimum number of options reached.")
                          : undefined
                      }
                    >
                      <Delete
                        className={`element-action ${
                          validated &&
                          ((typeof subLesson.title === "object"
                            ? subLesson?.title[locale]
                            : subLesson?.title) ?? "") === "" &&
                          "mb-4"
                        } ${data.subLessons.length <= 1 ? "disabled" : ""}`}
                        onClick={() => {
                          data.subLessons.length > 1 &&
                            setSubLessonDeleteId(index);
                        }}
                      />
                    </div>
                  </div>
                ),
              )}
              <div className="position-sticky bottom-0 bg-white important-attributes py-4">
                <Button
                  role="button"
                  variant="outline-editing"
                  className="w-100 fs-4 py-0"
                  onClick={() => handleUpdateSubLesson("", -1)}
                >
                  +
                </Button>
                {subLessonDeleteId !== undefined && (
                  <Alert
                    variant="danger"
                    className="d-flex flex-column flex-sm-row mt-4 mb-0"
                  >
                    <div className="flex-grow-1">
                      <Alert.Heading>
                        {_("Delete this Sub-lesson?")}
                      </Alert.Heading>
                      <p>
                        {typeof data.subLessons[subLessonDeleteId].title ===
                        "object"
                          ? data.subLessons[subLessonDeleteId].title[locale]
                          : data.subLessons[subLessonDeleteId].title}
                      </p>
                      <p>
                        <strong>{_("This action cannot be undone.")}</strong>
                      </p>
                    </div>
                    <div className="d-inline-block mt-auto flex-shrink-0 justify-content-end">
                      <Button
                        variant="outline-danger"
                        className="cancel me-2 mb-2"
                        onClick={() => {
                          setSubLessonDeleteId(undefined);
                        }}
                      >
                        {_("Cancel")}
                      </Button>
                      <Button
                        variant="danger"
                        className="mb-2"
                        onClick={() => {
                          removeSubLesson(subLessonDeleteId.toString());
                          setSubLessonDeleteId(undefined);
                        }}
                      >
                        {_("Delete")}
                      </Button>
                    </div>
                  </Alert>
                )}
              </div>
            </div>
          )}
          <ContentEditorFooter maxStages={2} />
        </Form>
      </Modal.Body>
    </>
  );
};

export default AddLesson;
